import React from 'react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // Check if it's a hot reload related error
    const hotReloadErrors = ['useAuth', 'useSession', 'usePermissions', 'useSocket', 'useAppearance'];
    const isHotReloadError = hotReloadErrors.some(hook => error.message?.includes(hook));
    
    if (isHotReloadError) {
      console.log('Hot reload error detected, attempting to recover...');
      // In development, try to recover
      if (process.env.NODE_ENV === 'development') {
        // Force a full page reload to fix context issues
        window.location.reload();
      }
    }
  }

  render() {
    if (this.state.hasError) {
      // Check if it's a development hot reload error
      if (this.state.error?.message?.includes('useAuth') || this.state.error?.message?.includes('useSession')) {
        return (
          <div className="min-h-screen bg-gray-50 flex items-center justify-center">
            <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Development Hot Reload Issue</h2>
              <p className="text-gray-600 mb-4">
                React hot reload encountered an issue. This is a development-only problem.
              </p>
              <button
                onClick={() => window.location.reload()}
                className="w-full bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors"
              >
                Refresh Page
              </button>
            </div>
          </div>
        );
      }

      // Generic error fallback
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full">
            <h2 className="text-xl font-bold text-red-600 mb-4">Something went wrong</h2>
            <p className="text-gray-600 mb-4">
              {this.state.error?.message || 'An unexpected error occurred.'}
            </p>
            <button
              onClick={() => {
                this.setState({ hasError: false, error: null });
              }}
              className="w-full bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors mr-2"
            >
              Try Again
            </button>
            <button
              onClick={() => window.location.reload()}
              className="w-full bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 transition-colors mt-2"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;